import { boolean, catch as zodCatch, object } from 'zod/mini';
import type { infer as Infer } from 'zod/mini';
import type { DashboardWidgetColumn, DashboardWidgetSettingsContext } from '@h20-shared/dashboards';
import {
    choiceField,
    columnField,
    DASHBOARD_VALUE_FORMATS,
    describeField,
    measureFields,
    perColumn,
    repeated,
    switchField,
    textField,
    withDefaults,
} from '@h20-shared/dashboards';
import { buildGridColumns } from './columns';
import { GRID_DEFAULT_VIEWS, GRID_MAX_VIEWS } from './dashboard-grid.config';

/** The columns the table draws, keyed by `colId`, so column settings match what the author sees. */
function gridColumnsForPanel(
    context: DashboardWidgetSettingsContext
): readonly DashboardWidgetColumn[] {
    const { queryKey } = context;
    if (queryKey === undefined) {
        return [];
    }
    const columns: DashboardWidgetColumn[] = [];
    // The headers stay i18n keys, because the settings panel translates its labels itself.
    for (const column of buildGridColumns(queryKey, undefined, (i18nKey) => i18nKey)) {
        if (column.hide !== true && column.colId !== undefined) {
            columns.push({
                key: column.colId,
                label: column.headerName ?? column.colId,
                // An empty position box shows the column's place in this list, counted from 1.
                defaults: { position: String(columns.length + 1) },
            });
        }
    }
    return columns;
}

/** One card per table column. `hidden` hides the column in every view. `position` is typed text */
const GRID_COLUMNS_SCHEMA = perColumn(
    zodCatch(
        object({
            hidden: switchField('Dashboard.Grid.Hide', false),
            position: zodCatch(textField('Dashboard.Grid.Position'), ''),
        }),
        ({ value }) => ({ hidden: value === true, position: '' })
    ),
    'Dashboard.Grid.Columns',
    gridColumnsForPanel
);

/** The number column grouping adds: the numerator, or the numerator over the denominator. */
const GRID_MAIN_VALUE_SCHEMA = describeField(
    object({
        label: textField('Dashboard.Grid.ColumnHeader'),
        position: textField('Dashboard.Grid.Position'),
        format: choiceField(DASHBOARD_VALUE_FORMATS, 'Dashboard.Grid.ShowAs'),
        numerator: describeField(
            object(
                measureFields(
                    'Dashboard.Grid.Column',
                    'Dashboard.Grid.CombineBy',
                    'Dashboard.Grid.OnlyIf'
                )
            ),
            { label: 'Dashboard.Grid.Numerator' }
        ),
        denominator: describeField(
            object({
                enabled: switchField('Dashboard.Grid.Denominator'),
                ...measureFields(
                    'Dashboard.Grid.Column',
                    'Dashboard.Grid.CombineBy',
                    'Dashboard.Grid.OnlyIf'
                ),
            }),
            { label: 'Dashboard.Grid.Denominator' }
        ),
    }),
    { label: 'Dashboard.Grid.MainValue' }
);

/** One button in the view switcher above the table; a `(none)` column keeps rows ungrouped. */
const GRID_VIEW_SCHEMA = object({
    label: textField('Dashboard.Grid.ViewSwitchLabel'),
    title: textField('Dashboard.Grid.ViewTitle'),
    description: textField('Dashboard.Grid.ViewDescription'),
    column: columnField('Dashboard.Grid.GroupColumn', (context) =>
        context.columns.map((column) => column.key)
    ),
    // Like `hidden` in `GRID_COLUMNS_SCHEMA`, but hides columns in this view only.
    columns: perColumn(
        zodCatch(boolean(), false),
        'Dashboard.Grid.ViewHiddenColumns',
        gridColumnsForPanel
    ),
});

const GRID_VIEW_BY_GROUP_SCHEMA = describeField(
    object({
        enabled: switchField('Dashboard.Grid.Grouping'),
        mainValue: GRID_MAIN_VALUE_SCHEMA,
        switchHeading: textField('Dashboard.Grid.SwitchHeading'),
        views: repeated(GRID_VIEW_SCHEMA, {
            max: GRID_MAX_VIEWS,
            fallback: GRID_DEFAULT_VIEWS,
            label: 'Dashboard.Grid.Views',
            itemLabel: 'Dashboard.Grid.View',
            countLabel: 'Dashboard.Grid.ViewCount',
        }),
    }),
    { label: 'Dashboard.Grid.Grouping' }
);

/** The settings panel */
export const GRID_CONFIG_SCHEMA = withDefaults(
    object({
        columns: GRID_COLUMNS_SCHEMA,
        grouping: GRID_VIEW_BY_GROUP_SCHEMA,
    })
);

export type GridViewConfig = Infer<typeof GRID_VIEW_SCHEMA>;
export type GridMainValueConfig = Infer<typeof GRID_MAIN_VALUE_SCHEMA>;
export type GridWidgetConfig = Infer<typeof GRID_CONFIG_SCHEMA>;
